import type { ICreateLocationCmd } from "@/data/commands/location/CreateLocationCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { Country, Language } from "@/data/model/Enums";
import type { ILocationDetail } from "@/data/model/Location";
import { exceptionTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";
import { Validation } from "@/data/util/Validation";

import { languageSelectList } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class LocationForm extends BaseEntityForm<ILocationDetail, ICreateLocationCmd> {
    private $nameField: JQuery;
    private $internalNameField: JQuery;
    private $slugField: JQuery;
    private $emailField: JQuery;
    private $websiteField: JQuery;
    private $phoneField: JQuery;
    private languageSelect: Select<Language>;

    // Invoice address
    private $companyField: JQuery;
    private $vatNrField: JQuery;
    private $billingCityField: JQuery;
    private $billingStreetField: JQuery;
    private $billingZipField: JQuery;
    private billingCountrySelect: Select<Country>;

    // Address
    private $cityField: JQuery;
    private $streetField: JQuery;
    private $zipField: JQuery;
    private countrySelect: Select<Country>;
    private $googleMapsLocationLink: JQuery;

    private originalSlug?: string;
    private slugValidationRegex = new RegExp("^(?:[a-z0-9]+(?:-[a-z0-9]+)*)?$");

    constructor(element: JQuery) {
        super(element, Factory.getInstance(LocationDao));

        this.$nameField = this.$form.find('.form-control[name="name"]').change(() => {
            const newName = this.$nameField.val();
            this.updateName(newName);

            const newSlug = this.slugify(newName);
            this.$slugField.val(newSlug);
        });

        this.$internalNameField = this.$form.find('.form-control[name="internal-name"]');
        this.$slugField = this.$form.find('.form-control[name="slug"]');
        this.$emailField = this.$form.find('.form-control[name="email"]');
        this.$phoneField = this.$form.find('.form-control[name="phone"]');

        const languageSelect = this.$form.find('.form-control[name="language"]');
        languageSelectList.forEach((item) => {
            languageSelect.append(`<option value="${item.id}">${i18n(`enums.Language.${item.name}`)}</option>`);
        });
        this.languageSelect = Select.createFromSelect(languageSelect);

        this.$websiteField = this.$form.find('.form-control[name="website"]');

        // Billing address
        this.$companyField = this.$form.find('.form-control[name="company"]');
        this.$vatNrField = this.$form.find('.form-control[name="vatnumber"]');
        this.$billingCityField = this.$form.find('.form-control[name="billing-address-city"]');
        this.$billingStreetField = this.$form.find('.form-control[name="billing-address-street"]');
        this.$billingZipField = this.$form.find('.form-control[name="billing-address-zip"]');
        this.$googleMapsLocationLink = this.$form.find('.form-control[name="google-maps-location-link"]');

        const billingCountrySelect = this.$form.find('.form-control[name="billing-address-country"]');
        Misc.eachEnum(Country, (i, e) => {
            billingCountrySelect.append('<option value="' + i + '">' + i18n("enums.Country." + e) + "</option>");
        });
        this.billingCountrySelect = Select.createFromSelect(billingCountrySelect);

        // Address
        this.$cityField = this.$form.find('.form-control[name="city"]');
        this.$streetField = this.$form.find('.form-control[name="street"]');
        this.$zipField = this.$form.find('.form-control[name="zip"]');

        const countrySelect = this.$form.find('.form-control[name="country"]');
        Misc.eachEnum(Country, (i, e) => {
            countrySelect.append('<option value="' + i + '">' + i18n("enums.Country." + e) + "</option>");
        });

        this.countrySelect = Select.createFromSelect(countrySelect);
    }

    public setFormData(model?: ILocationDetail) {
        this.updateName(model === undefined ? "" : getLocationName(model));
        this.$internalNameField.val(model?.internalName ?? "");
        
        this.originalSlug = model === undefined ? "" : model?.slug === undefined ? this.slugify(model!.name ?? "") : model.slug;
        this.$slugField.val(this.originalSlug);

        const userLanguage = TastierGlobal.getIdentity()?.lang ?? Language.English;
        this.languageSelect.setValue(model === undefined ? userLanguage : model.language);
        this.$websiteField.val(model === undefined || model.website === undefined ? "" : model.website);
        this.$emailField.val(model === undefined || model.email === undefined ? "" : model.email);
        this.$phoneField.val(model === undefined || model.phoneNumber === undefined ? "" : model.phoneNumber);

        // Address
        const address = model?.address;
        this.$cityField.val(address === undefined ? "" : address.city!);
        this.$streetField.val(address === undefined ? "" : address.street!);
        this.$zipField.val(address === undefined ? "" : address.zip!);
        this.countrySelect.setValue(address === undefined ? undefined : address.country + "");

        // Billing address
        this.$companyField.val(model === undefined ? "" : model.company);
        this.$billingCityField.val(model === undefined ? "" : model.billingAddress.city!);
        this.$billingStreetField.val(model === undefined ? "" : model.billingAddress.street!);
        this.$billingZipField.val(model === undefined ? "" : model.billingAddress.zip!);
        this.billingCountrySelect.setValue(model === undefined ? undefined : model.billingAddress.country + "");
        this.$vatNrField.val(model === undefined || model.vatNumber === undefined ? "" : model.vatNumber);
        this.$googleMapsLocationLink.val(model == null ? "" : model.googleMapsLocationLink!);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateLocationCmd {
        const data = {
            name: this.$nameField.val().length > 0 ? this.$nameField.val() : undefined,
            internalName: this.$internalNameField.val().length > 0 ? this.$internalNameField.val() : undefined,
            slug: this.$slugField.val().length > 0 ? this.$slugField.val() : undefined,
            company: this.$companyField.val().length > 0 ? this.$companyField.val() : undefined,
            website: this.$websiteField.val().length > 0 ? this.$websiteField.val() : undefined,
            email: this.$emailField.val().length > 0 ? this.$emailField.val() : undefined,
            phoneNumber: this.$phoneField.val().length > 0 ? this.$phoneField.val() : undefined,
            vatNumber: this.$vatNrField.val().length > 0 ? this.$vatNrField.val() : undefined,

            billingStreet: this.$billingStreetField.val().length > 0 ? this.$billingStreetField.val() : undefined,
            billingZip: this.$billingZipField.val().length > 0 ? this.$billingZipField.val() : undefined,
            billingCity: this.$billingCityField.val().length > 0 ? this.$billingCityField.val() : undefined,
            billingCountry:
                this.billingCountrySelect.getValue() === undefined
                    ? undefined
                    : parseInt(this.billingCountrySelect.getValue()!),

            street: this.$streetField.val().length > 0 ? this.$streetField.val() : undefined,
            zip: this.$zipField.val().length > 0 ? this.$zipField.val() : undefined,
            city: this.$cityField.val().length > 0 ? this.$cityField.val() : undefined,
            country: this.countrySelect.getValue() === undefined ? undefined : parseInt(this.countrySelect.getValue()!),

            language: parseInt(this.languageSelect.getValue()!),
            googleMapsLocationLink:
                this.$googleMapsLocationLink.val().length > 0 ? this.$googleMapsLocationLink.val() : undefined,
        };

        let hasError = false;

        if (data.name === undefined) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        }

        if (data.slug != null && !this.slugValidationRegex.test(data.slug)) {
            hasError = true;
            this.$slugField.addClass("is-invalid");
        }

        if (data.company === undefined) {
            hasError = true;
            this.$companyField.addClass("is-invalid");
        }

        if (data.billingStreet === undefined) {
            hasError = true;
            this.$billingStreetField.addClass("is-invalid");
        }

        if (data.billingCity === undefined) {
            hasError = true;
            this.$billingCityField.addClass("is-invalid");
        }

        if (data.website !== undefined && !Validation.isUrl(data.website)) {
            hasError = true;
            this.$websiteField.addClass("is-invalid");
        }

        if (data.email !== undefined && !Validation.isEmail(data.email)) {
            hasError = true;
            this.$emailField.addClass("is-invalid");
        }

        if (data.phoneNumber !== undefined && !Validation.isInternationalPhoneNumber(data.phoneNumber)) {
            hasError = true;
            this.$phoneField.addClass("is-invalid");
        }

        if (!Validation.isZip(data.billingZip)) {
            hasError = true;
            this.$billingZipField.addClass("is-invalid");
        }

        if (data.billingCountry === undefined || isNaN(data.billingCountry)) {
            hasError = true;
            this.billingCountrySelect.setError(true);
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (hasError ? undefined : data) as any;
    }

    private slugify(str: string) {
        return String(str)
            .normalize("NFKD") // split accented characters into their base characters and diacritical marks
            .replace(/[\u0300-\u036f]/g, "") // remove all the accents, which happen to be all in the \u03xx UNICODE block.
            .trim() // trim leading or trailing whitespace
            .toLowerCase() // convert to lowercase
            .replace(/[^a-z0-9 -]/g, "") // remove non-alphanumeric characters
            .replace(/\s+/g, "-") // replace spaces with hyphens
            .replace(/-+/g, "-"); // remove consecutive hyphens
    }
    
    // the copy is created on the server right away and opened, rather than prefilling a new location
    protected copySaved(model: ILocationDetail) {
        Factory.getInstance<LocationDao>(LocationDao)
            .copy(model.id)
            .then((copy) => {
                location.href = this.getBasePath() + copy.id;
            })
            .catch((ex) => {
                logError(ex);
                this.setFormData(model);
                this.setLoading(false);
                UiNotification.showException(i18n("location.copyFailed"));
            });
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.locationHasOrdersOrPaymentsException:
                return i18n("location.locationHasOrdersOrPayments");
                
            case exceptionTypes.internalNameAlreadyInUseByAnotherLocation:
                this.$internalNameField.addClass("is-invalid");
                this.scrollToFirstErrorField();
                return i18n("location.childLocations.internalNameAlreadyInUseByAnotherLocationError");
                
            case "location_slug_not_available_exception":
                this.$slugField.addClass("is-invalid");
                this.scrollToFirstErrorField();

                return i18n("location.locationSlugNotAvailable", {
                    slug: b.additionalData.slug,
                    proposedSlugs: b.additionalData.proposedSlugs,
                });
        }

        return b.additionalData.reason;
    }
}
